import React from 'react';
import { Mail, Phone, Wrench } from 'lucide-react';
import MoleculeLoader from './MoleculeLoader';

/**
 * Full-screen "We'll be back soon" page used when an admin flips
 * `maintenance_mode` ON in site settings. Includes contact links so
 * high-intent customers can still reach the team during downtime.
 *
 * Designed to feel on-brand (teal palette, molecule loader, no AI-slop
 * gradient spam) and to read well on the smallest phones.
 */
const MaintenancePage = ({ message, contactEmail, contactPhone }) => {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-10"
      data-testid="maintenance-page"
    >
      <div className="max-w-xl w-full text-center">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-teal-100 text-teal-700 mb-6">
          <Wrench className="h-7 w-7" />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
          We'll be back soon.
        </h1>
        <p
          className="text-base text-slate-600 mb-8 leading-relaxed"
          data-testid="maintenance-message"
        >
          {message || "We're performing scheduled maintenance on Genora Peptides. Please check back in a little while."}
        </p>

        <div className="flex justify-center mb-10">
          <MoleculeLoader size={88} label="" />
        </div>

        {(contactEmail || contactPhone) && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 text-left">
            <p className="text-sm font-semibold text-slate-700 mb-3 text-center">
              Need to reach us in the meantime?
            </p>
            <div className="space-y-2.5">
              {contactEmail && (
                <a
                  href={`mailto:${contactEmail}`}
                  className="flex items-center gap-3 text-slate-700 hover:text-teal-700 transition-colors"
                  data-testid="maintenance-contact-email"
                >
                  <Mail className="h-4 w-4 text-teal-600 shrink-0" />
                  <span className="text-sm font-medium break-all">{contactEmail}</span>
                </a>
              )}
              {contactPhone && (
                <a
                  href={`tel:${contactPhone.replace(/[^+\d]/g, '')}`}
                  className="flex items-center gap-3 text-slate-700 hover:text-teal-700 transition-colors"
                  data-testid="maintenance-contact-phone"
                >
                  <Phone className="h-4 w-4 text-teal-600 shrink-0" />
                  <span className="text-sm font-medium">{contactPhone}</span>
                </a>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-slate-400 mt-8">© Genora Peptides</p>
      </div>
    </div>
  );
};

export default MaintenancePage;
